import React from 'react';
import GridItem from '../../../layout/GridComponents/GridItem';
import CustomTable from '../../../tables/CustomTable';
import { CustomColumn } from '../../../../types/components/tables/tableTypes';
import { formatESGWithUnit } from '../../../../utilities/numberFormatters';
import RaptorStatusBox from '../../../feedback/RaptorStatusBox.component';
import { Status } from '../../../../types/redux/data/dataTypes';
import {
  IndicatorComplianceTableData,
  IndicatorComplianceUnderlyingTableData,
} from './IndicatorCompliance.component';
import RaptorLoading from '../../../feedback/RaptorLoading';
import makeStyles from '@mui/styles/makeStyles';
import EditValueModal from './EditValueModal.component';
import { ClassNameMap } from '@mui/material';
import {
  useIndicatorComplianceQuery,
  type IndicatorCompliancePayload,
  type IndicatorComplianceRow,
} from '../../../../api/rs-hg/esg';

const useStyles = makeStyles(() => ({
  editableValueContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '0.62rem',
  },
}));

interface SecondLevelTableProps {
  data: IndicatorCompliancePayload;
  rowData: IndicatorComplianceTableData;
  positionDate: string | undefined;
  fundId: string;
}

const buildStandardColumns = (
  onChange: () => void,
  manualOverwrite: boolean,
  unit: string,
  classes: ClassNameMap,
): CustomColumn<IndicatorComplianceUnderlyingTableData>[] => [
  {
    title: 'Position Name',
    field: 'position_name',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
    width: '35%',
  },
  {
    title: 'Indicator',
    field: 'indicator',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
    width: '15%',
  },
  {
    title: 'Involvement',
    field: 'involvement',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
    width: '15%',
  },
  {
    title: 'Value',
    field: 'value',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
    render: (rowData) => {
      if (manualOverwrite === true) {
        return (
          <div className={classes.editableValueContainer}>
            <EditValueModal
              gmid={rowData.gmid}
              indicator={rowData.indicator}
              value={rowData.value}
              unit={unit}
              onChange={onChange}
              isEdited={
                rowData.is_edited !== undefined ? rowData.is_edited : false
              }
            />
          </div>
        );
      } else {
        return formatESGWithUnit(rowData.value, unit);
      }
    },
    width: '15%',
  },
  {
    title: 'Pass / Fail',
    field: 'pass_fail',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
    render: (rowData) => (
      <RaptorStatusBox status={rowData.pass_fail as Status} />
    ),
    width: '15%',
  },
];

const buildColumns = (
  data: IndicatorCompliancePayload,
  columns: CustomColumn<IndicatorComplianceUnderlyingTableData>[],
): CustomColumn<IndicatorComplianceUnderlyingTableData>[] => {
  if (!data.configs?.position_columns) {
    return columns;
  }

  const returnColumns: CustomColumn<IndicatorComplianceUnderlyingTableData>[] =
    [];
  data.configs.position_columns.forEach((column: string) => {
    columns.forEach(
      (col: CustomColumn<IndicatorComplianceUnderlyingTableData>) => {
        if (col.field === column) {
          returnColumns.push(col);
        }
      },
    );
  });
  return returnColumns;
};

const buildTableData = (
  indicator: string,
  rows: IndicatorComplianceRow[],
): IndicatorComplianceUnderlyingTableData[] => {
  if (!rows?.length) {
    return [];
  }

  let returnData: IndicatorComplianceUnderlyingTableData[] = [];
  rows.forEach((row) => {
    if (row.indicator === indicator) {
      returnData = (row.underlying ??
        []) as IndicatorComplianceUnderlyingTableData[];
    }
  });
  return returnData;
};

const SecondLevelTable: React.FC<SecondLevelTableProps> = ({
  data,
  rowData,
  positionDate,
  fundId,
}) => {
  // Shared cache with the parent page; refetch only after user edits.
  const indicatorComplianceQuery = useIndicatorComplianceQuery(
    fundId,
    positionDate,
  );

  const classes = useStyles();

  const { data: pageModel, isFetching, refetch } = indicatorComplianceQuery;
  const payload = pageModel?.data ?? data;
  const secondLevelTable = buildTableData(
    rowData.indicator,
    payload.data ?? [],
  );

  const refetchAfterEdit = () => {
    void refetch();
  };

  return (
    <GridItem xs={12} card>
      {isFetching && secondLevelTable.length <= 0 ? (
        <RaptorLoading />
      ) : (
        <CustomTable<IndicatorComplianceUnderlyingTableData>
          columns={buildColumns(
            payload,
            buildStandardColumns(
              refetchAfterEdit,
              payload.configs.manual_overwrite,
              rowData.unit,
              classes,
            ),
          )}
          showToolbar
          data={secondLevelTable}
          title={'Underlying Positions'}
          options={{
            draggable: false,
            sorting: false,
            paging: false,
            search: true,
            exportButton: true,
          }}
        />
      )}
    </GridItem>
  );
};

export default SecondLevelTable;
